// src/pages/CandidateTerms.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const CandidateTerms = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-user-graduate text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Candidate Terms & Conditions
                </h1>
                <p className="text-gray-600">
                    Terms specific to candidates and job seekers using GradPath
                </p>
                <p className="text-sm text-gray-500 mt-2">Last updated: June 27, 2026</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
                <section className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                    <p className="text-yellow-800 text-sm">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        These terms apply specifically to candidates and job seekers using GradPath.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-check-circle text-gradpath-green text-lg"></i>
                        Acceptance of Terms
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        By creating a candidate account and using GradPath's job search services, you agree to be bound by these Candidate Terms & Conditions, our main Terms & Conditions, Privacy Policy, and all applicable laws.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-id-card text-gradpath-green text-lg"></i>
                        Profile Accuracy
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        Candidates must provide accurate and truthful information in their profiles, including:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Personal information (name, contact details)</li>
                        <li>Education and qualifications</li>
                        <li>Work experience and skills</li>
                        <li>Career preferences and requirements</li>
                    </ul>
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-700">
                            <i className="fas fa-exclamation-circle mr-2"></i>
                            <strong>Warning:</strong> Providing false or misleading information may result in account termination and being reported to potential employers.
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-share-alt text-gradpath-green text-lg"></i>
                        Data Sharing Consent
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        By creating a candidate profile, you expressly consent to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Your profile information being shared with recruiters and companies using GradPath</li>
                        <li>Recruiters viewing, searching, and contacting you based on your qualifications</li>
                        <li>Your information being used for job matching and recommendations</li>
                    </ul>

                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green text-lg"></i>
                        Marketing Communications
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        By creating an account, you consent to receive communications from GradPath, including:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Job alerts and recommendations</li>
                        <li>Career advice and industry insights</li>
                        <li>Platform updates and feature announcements</li>
                        <li>Application status updates</li>
                        <li>Special offers and promotions</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        You can opt-out of marketing emails at any time by clicking the "unsubscribe" link at the bottom of our emails or updating your communication preferences in your account settings.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-handshake text-gradpath-green text-lg"></i>
                        Professional Conduct
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        Candidates must conduct themselves professionally when interacting with recruiters and employers through the platform:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Respond to recruiter inquiries in a timely manner</li>
                        <li>Be honest and transparent about qualifications and experience</li>
                        <li>Respect recruiters' time and communication preferences</li>
                        <li>Do not engage in harassment or abusive behavior</li>
                        <li>Do not misuse the platform for non-recruitment purposes</li>
                    </ul>
                    <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-red-700">
                            <i className="fas fa-exclamation-circle mr-2"></i>
                            <strong>Prohibited:</strong> Harassment, abusive behavior, or misuse of the platform will result in account termination.
                        </p>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-briefcase text-gradpath-green text-lg"></i>
                        Job Applications
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        GradPath is a job aggregation and talent sourcing platform. We are not responsible for:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>The application process or hiring decisions of employers</li>
                        <li>The content, accuracy, or legitimacy of external job postings</li>
                        <li>Any issues arising from your communication with employers</li>
                        <li>Employment outcomes or job placement</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Recommendation:</strong> Always verify job details directly with the employer and be cautious of potential job scams.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-user-shield text-gradpath-green text-lg"></i>
                        Account Termination
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        GradPath reserves the right to suspend or terminate candidate accounts for:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Providing false or misleading information</li>
                        <li>Harassing or abusing recruiters or other users</li>
                        <li>Misusing the platform for non-recruitment purposes</li>
                        <li>Violating these terms or applicable laws</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-flag text-gradpath-green text-lg"></i>
                        Reporting Issues
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        If you encounter suspicious recruiters, fraudulent job postings, or inappropriate behavior:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Report to <a href="mailto:compliance@gradpath.co.za" className="text-gradpath-green hover:underline">compliance@gradpath.co.za</a></li>
                        <li>Provide job posting links or recruiter details</li>
                        <li>All reports are investigated promptly</li>
                        <li>Reports are kept confidential</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-edit text-gradpath-green text-lg"></i>
                        Changes to These Terms
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We may modify these Candidate Terms at any time. Changes become effective immediately upon posting. Your continued use of the Platform after changes constitutes acceptance of the updated terms.
                    </p>
                </section>

                <section className="bg-gray-50 p-4 rounded-lg">
                    <h2 className="text-lg font-bold text-gradpath-dark mb-2 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green"></i>
                        Contact Information
                    </h2>
                    <p className="text-gray-700 text-sm">
                        For questions about these Candidate Terms, please contact:
                    </p>
                    <p className="text-gray-700 text-sm mt-2">
                        <strong>Support:</strong> <a href="mailto:support@gradpath.co.za" className="text-gradpath-green hover:underline">support@gradpath.co.za</a><br />
                        <strong>Compliance:</strong> <a href="mailto:compliance@gradpath.co.za" className="text-gradpath-green hover:underline">compliance@gradpath.co.za</a>
                    </p>
                </section>

                <div className="text-center pt-4">
                    <Link to="/" className="inline-flex items-center gap-2 text-gradpath-green hover:underline">
                        <i className="fas fa-arrow-left"></i>
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CandidateTerms;